import { useRef, forwardRef, useImperativeHandle } from 'react';
import { Paper, Theme, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { dividerColor, grey } from 'src/data/colors';
import { StatusProps } from './Status';
import { Equipment } from 'src/models';
import CardEquipment from './CardEquipment';
import identifiers from 'src/tests/identifiers';
import CardLink from './CardLink';

interface InverterCardProps {
  componentId: string;
  badge?: string;
  className?: string;
  color?: string;
  status?: Omit<StatusProps, 'color'>;
  gridArea?: string;
  zIndex?: number;
  equipments?: Equipment[][];
  siteId: number;
  linkTagsIds?: string[];
  isResidentialSite: boolean;
}

const useStyles = makeStyles<Theme, { border: boolean; color: string }>(() => ({
  cardContainer: {
    width: '100%',
  },
  paper: {
    border: ({ color }) => `1px solid ${color}`,
    position: 'relative',
    minWidth: '278px',
    height: '80px',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0px 4px 8px -4px rgba(0, 0, 0, 0.16), 0px 8px 12px -4px rgba(0, 0, 0, 0.08)',
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  header: {
    padding: '0 10px',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
  },
  equipmentContainer: {
    padding: '0 10px',
    marginTop: 8,
    '@media (max-width: 712px)': {
      display: 'none',
    },
  },
  notInstalledText: {
    color: grey,
    fontSize: 20,
    fontWeight: 500,
  },
  blackBold: {
    color: 'black',
    fontSize: 22,
  },
}));

const getCardContent = (
  equipments: Equipment[][] | undefined,
  isResidentialSite: boolean,
  cssClass: Record<string, string>
) => {
  if (equipments?.length) {
    return <CardEquipment equipments={equipments || []} size="large" />;
  }

  if (isResidentialSite) {
    return <Typography className={cssClass.notInstalledText}>Inverter</Typography>;
  }

  return (
    <Box style={{ width: '100%', textAlign: 'center' }}>
      <Typography className={cssClass.blackBold}>Inverter</Typography>
    </Box>
  );
};

const InverterCard = forwardRef<HTMLDivElement, InverterCardProps>(
  (
    {
      className,
      color,
      status,
      equipments,
      gridArea,
      zIndex,
      componentId,
      siteId,
      linkTagsIds,
      isResidentialSite,
    },
    ref
  ) => {
    const classes = useStyles({ border: !!status, color: color || dividerColor });
    const localPaperRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => localPaperRef.current as HTMLDivElement);

    return (
      <Paper
        data-testid={identifiers.dashboardPage.flowComponent + '-' + componentId}
        className={`${className || ''} ${classes.paper}`}
        ref={localPaperRef}
        variant="outlined"
        style={{
          gridArea: gridArea,
          zIndex: zIndex,
          position: 'relative',
        }}
      >
        <CardLink linkTagsIds={linkTagsIds} siteId={siteId} componentId={componentId}>
          {getCardContent(equipments, isResidentialSite, classes)}
        </CardLink>
      </Paper>
    );
  }
);

export default InverterCard;
